import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { MaterialPricingConfig } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import SimpleFormModal from '../../components/SimpleFormModal';
import './AdminMaterialPricing.css';

const AdminMaterialPricing: React.FC = () => {
  const [configs, setConfigs] = useState<MaterialPricingConfig[]>([]);
  const [selectedMaterial, setSelectedMaterial] = useState<string>('Canvas');
  const [config, setConfig] = useState<MaterialPricingConfig>({
    material: 'Canvas',
    rollWidthInches: 0,
    rollLengthInches: 0,
    rollCost: 0,
    wastePercent: 0,
    markupPercent: 0,
    isActive: true
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [recalculating, setRecalculating] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();

  const fetchConfig = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<MaterialPricingConfig[]>('/admin/material-pricing');
      setConfigs(response.data);
      const initial = response.data.find(c => c.material === selectedMaterial) || response.data[0];
      if (initial) {
        setSelectedMaterial(initial.material);
        setConfig(initial);
      }
    } catch (error) {
      console.error('Failed to fetch material pricing config:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMaterial]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async (): Promise<void> => {
    if (!config.material?.trim()) {
      warning('Material name is required.');
      return;
    }
    if (config.rollWidthInches <= 0 || config.rollLengthInches <= 0 || config.rollCost <= 0) {
      warning('Roll width, length, and cost must be greater than 0.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/material-pricing', config);
      const response = await api.get<MaterialPricingConfig[]>('/admin/material-pricing');
      setConfigs(response.data);
      success('Material pricing updated.');
    } catch (error) {
      showError('Failed to save material pricing.');
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = async (): Promise<void> => {
    setRecalculating(true);
    try {
      await api.post(`/admin/material-pricing/recalculate-sizes?material=${encodeURIComponent(config.material)}`);
      success('Size pricing defaults recalculated.');
    } catch (error) {
      showError('Failed to recalculate size pricing.');
    } finally {
      setRecalculating(false);
    }
  };

  const handleMaterialSelect = (material: string): void => {
    setSelectedMaterial(material);
    const found = configs.find(c => c.material === material);
    if (found) {
      setConfig(found);
    } else {
      setConfig({
        material,
        rollWidthInches: 0,
        rollLengthInches: 0,
        rollCost: 0,
        wastePercent: 0,
        markupPercent: 0,
        isActive: true
      });
    }
  };

  const addMaterial = (): void => {
    setShowAddModal(true);
  };

  const handleAddMaterial = (values: Record<string, string>): void => {
    const name = values.material?.trim();
    if (!name) {
      warning('Material name is required.');
      return;
    }
    if (configs.some(c => c.material.toLowerCase() === name.toLowerCase())) {
      warning('Material already exists.');
      return;
    }
    handleMaterialSelect(name);
    setShowAddModal(false);
  };

  if (loading) {
    return <div className="loading">Loading material pricing...</div>;
  }

  return (
    <div className="admin-material-pricing">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1>Material Pricing</h1>
          </div>
        </div>

        <div className="pricing-card">
          <div className="material-header">
            <h2>Material Pricing</h2>
            <div className="material-actions">
              <select
                value={selectedMaterial}
                onChange={(e) => handleMaterialSelect(e.target.value)}
              >
                {configs.map((c) => (
                  <option key={c.material} value={c.material}>{c.material}</option>
                ))}
                {!configs.find(c => c.material === selectedMaterial) && (
                  <option value={selectedMaterial}>{selectedMaterial}</option>
                )}
              </select>
              <button className="btn btn-outline" onClick={addMaterial}>Add Material</button>
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Roll Width (inches)</label>
              <input
                type="number"
                step="0.01"
                value={config.rollWidthInches}
                onChange={(e) => setConfig({ ...config, rollWidthInches: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="form-group">
              <label>Roll Length (inches)</label>
              <input
                type="number"
                step="0.01"
                value={config.rollLengthInches}
                onChange={(e) => setConfig({ ...config, rollLengthInches: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="form-group">
              <label>Roll Cost ($)</label>
              <input
                type="number"
                step="0.01"
                value={config.rollCost}
                onChange={(e) => setConfig({ ...config, rollCost: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="form-group">
              <label>Markup Percent (%)</label>
              <input
                type="number"
                step="0.1"
                value={config.markupPercent}
                onChange={(e) => setConfig({ ...config, markupPercent: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Waste Percent (%)</label>
            <div className="range-row">
              <input
                type="range"
                min="0"
                max="50"
                step="0.5"
                value={config.wastePercent}
                onChange={(e) => setConfig({ ...config, wastePercent: parseFloat(e.target.value) || 0 })}
              />
              <span className="range-value">{config.wastePercent.toFixed(1)}%</span>
            </div>
          </div>

          <div className="actions-row">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving && <span className="btn-spinner" aria-hidden="true"></span>}
              {saving ? 'Saving...' : 'Save Pricing'}
            </button>
            <button className="btn btn-outline" onClick={handleRecalculate} disabled={recalculating}>
              {recalculating && <span className="btn-spinner" aria-hidden="true"></span>}
              Recalculate Size Defaults
            </button>
          </div>
        </div>
      </div>
      <SimpleFormModal
        isOpen={showAddModal}
        title="Add Material"
        fields={[
          { name: 'material', label: 'Material Name', required: true, placeholder: 'Canvas' }
        ]}
        submitLabel="Add Material"
        onSubmit={handleAddMaterial}
        onClose={() => setShowAddModal(false)}
      />
    </div>
  );
};

export default AdminMaterialPricing;
